/**
 * SetsPage - Displays sets within a specific Pokemon series
 * Shows set cards with navigation to individual set card listings
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import analyticsService from '../services/analyticsService';
import PageMeta from '../components/PageMeta';
import { SetCoverArt } from '../components/SetArt';
import { gameLabel, gameOfId } from '../lib/games';

const SetsOfSeriesPage = () => {
  // Get the series ID from URL parameter
  const { seriesId } = useParams();
  
  // State
  const [seriesData, setSeriesData] = useState(null);
  const [setsData, setSetsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('newest');

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const seriesName = seriesData?.name;
  const title = seriesName
    ? `${seriesName} Sets | Lilycove`
    : seriesId
      ? `Series ${seriesId} Sets | Lilycove`
      : 'Series Sets | Lilycove';

  // Track exit when component unmounts
  useEffect(() => {
    return () => {
      // Track series view exit with actual duration
      analyticsService.trackSeriesViewExit(seriesId);
    };
  }, [seriesId]);

  // Fetch series and sets data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch series data first to get the series name
        const seriesResponse = await fetch(`${apiUrl}/api/series/${seriesId}`);
        if (!seriesResponse.ok) {
          throw new Error('Network response was not ok for series data');
        }
        const seriesData = await seriesResponse.json();
        setSeriesData(seriesData);
        
        // Track series view
        analyticsService.trackSeriesView(seriesId);
        
        // Then fetch the sets for this series
        const setsResponse = await fetch(`${apiUrl}/api/series/${seriesId}/sets`);
        if (!setsResponse.ok) {
          throw new Error('Network response was not ok for sets data');
        }
        const sets = await setsResponse.json();
        setSetsData(sets);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (seriesId) {
      fetchData();
    }
  }, [seriesId, apiUrl]);

  // Filter and sort sets
  const getFilteredAndSortedSets = () => {
    // Filter by search query
    let filtered = setsData.filter(set => 
      set.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Sort based on selected option
    switch(sortOption) {
      case 'newest':
        return [...filtered].sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
      case 'oldest':
        return [...filtered].sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate));
      case 'name-asc':
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return [...filtered].sort((a, b) => b.name.localeCompare(a.name));
      case 'cards-high':
        return [...filtered].sort((a, b) => b.total - a.total);
      case 'cards-low':
        return [...filtered].sort((a, b) => a.total - b.total);
      default:
        return filtered;
    }
  };

  // Handle set click with analytics tracking
  const handleSetClick = async (set) => {
    // Track navigation intent separately from set page views.
    await analyticsService.trackSetNavigation(set.id, seriesId, 'series_page');
    
    // Track navigation context
    analyticsService.trackNavigation(
      `/series/${seriesId}`, 
      `/sets/${set.id}`, 
      'set_card'
    );
    
    // Allow normal navigation to proceed
  };

  // Render set card
  const renderSetCard = (set) => {
    // Format the release date
    const releaseDate = new Date(set.releaseDate);
    const formattedDate = releaseDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    return (
      <a 
        key={set.id} 
        href={`/sets/${set.id}`}
        onClick={() => handleSetClick(set)}
        className="bg-dark border border-purple/20 rounded-lg overflow-hidden hover:border-purple/60 transition-all duration-300 hover:-translate-y-1"
      >        <div className="relative">
          {set.LogoUrl ? (
          <img 
            src={set.LogoUrl} 
            alt={set.name} 
            className="w-[90%] h-48 object-contain mx-auto"
            onError={(e) => {
              if (set.backupLogoUrl) {
                e.target.src = set.backupLogoUrl;
              } else {
                e.target.src = '/placeholder-card.svg';
              }
            }}
          />
          ) : (
            // WO-30: other games' sets: their cover card (a card we host), or a drawn tile
            <SetCoverArt set={set} gameId={set.game || gameOfId(set.id)} className="h-48" />
          )}
          <div className="absolute top-2 right-2 bg-dark/80 text-xs font-medium text-skyblue px-2 py-1 rounded">
            {set.ptcgoCode || 'TCG'}
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-lg font-medium text-cream mb-1">{set.name}</h3>
          <div className="flex items-center text-xs text-cream/60 mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {formattedDate}
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center text-xs text-green">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path>
              </svg>
              {set.total} Cards
            </div>
            <span className="text-xs text-skyblue hover:text-cream transition-colors">View Set →</span>
          </div>
        </div>
      </a>
    );
  };

  // Render loading state
  const renderLoading = () => (
    <div className="col-span-full flex justify-center p-8">
      <div className="animate-pulse flex flex-col items-center">
        <div className="h-8 w-32 bg-purple/20 rounded mb-2"></div>
        <div className="h-4 w-48 bg-purple/10 rounded"></div>
      </div>
    </div>
  );

  // Render empty state
  const renderEmpty = () => (
    <div className="col-span-full text-center p-8">
      <p className="text-cream/70">No sets found matching your search criteria.</p>
    </div>
  );

  // Render sets grid
  const renderSetsGrid = () => {
    const filteredSets = getFilteredAndSortedSets();
    
    if (filteredSets.length === 0) {
      return renderEmpty();
    }
    
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSets.map(set => renderSetCard(set))}
      </div>
    );
  };

  return (
    <>
      <PageMeta
        title={title}
        description={
          seriesName
            ? `Explore ${seriesName} sets on Lilycove.`
            : 'Explore Pokemon TCG sets on Lilycove.'
        }
        // WO-42: the server's canonical (no query string, no www.), so its JSON-LD for this URL is kept (PageMeta)
        url={seriesId ? `https://lilycove.io/series/${encodeURIComponent(seriesId)}` : undefined}
      />
      {/* Search Bar (Mobile) */}
      <div className="relative mb-8 md:hidden">
        <input 
          type="text" 
          placeholder="Search for sets..." 
          className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-cream placeholder:text-cream/50"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-5 w-5 text-purple">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.3-4.3"></path>
        </svg>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center text-sm mb-6 text-cream/70">
        <Link to="/" className="text-skyblue hover:text-cream">Home</Link>
        <span className="mx-2">/</span>
        {/* WO-30: other games' year groups (ygo.2026, mtg.2026, ...) named their game, not Pokemon */}
        <Link to={gameOfId(seriesId) === 'pokemon' ? '/series' : `/series?game=${gameOfId(seriesId)}`}
              className="text-skyblue hover:text-cream">{gameLabel(gameOfId(seriesId))} Series</Link>
        <span className="mx-2">/</span>
      </div>

      {/* Series Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl tracking-wider uppercase mb-1 font-medium text-purple">
            {isLoading ? 'Loading...' : `${seriesData?.name} Series Sets`}
          </h1>
          <p className="text-cream/70 text-sm tracking-wide">
            Browse all sets in the {seriesData?.name} series
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="relative w-full sm:w-64">
            <input 
              type="text" 
              id="sets-search"
              placeholder="Search sets" 
              className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-cream placeholder:text-cream/50"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-4 w-4 text-purple/70">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </div>

          <div className="flex gap-3">
            <div className="relative inline-block w-[140px]">
              <select 
                id="sort-select" 
                className="w-full h-9 rounded-md border border-purple/30 bg-dark px-3 py-1 text-cream appearance-none pr-8"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name-asc">Name: A-Z</option>
                <option value="name-desc">Name: Z-A</option>
                <option value="cards-high">Most Cards</option>
                <option value="cards-low">Fewest Cards</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cream">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid gap-6">
        {/* Sets Grid */}
        <div className="lg:col-span-3">
          {isLoading ? renderLoading() : renderSetsGrid()}
        </div>
      </div>
    </>
  );
};

export default SetsOfSeriesPage;
